import { ReactNode } from "react"
import { createPortal } from "react-dom"

const DEFAULT_ACCEPT_CLASS = "sm-button sm-button-purple"
const DEFAULT_AUX_CLASS = "sm-button sm-button-purple-outline"

export interface SmModalPropsBase {
  /**
   * The ID used to identify this component in Dash callbacks.
   */
  id?: string
  /**
   * Header of the modal
   */
  header?: string
  /**
   * Label to show in the 'accept' button (default: 'Accept')
   */
  accept?: string
  /**
   * Boolean to make the header sticky at the top of the modal (default: False)
   */
  sticky_header?: boolean
  /**
   * Boolean to make the action button sticky at the bottom of the modal (default: False)
   */
  sticky_bottom?: boolean
  /**
   * Boolean to enable the 'accept' button (default: True)
   */
  accept_enabled?: boolean
  /**
   * Label to show in the 'cancel' button (default: 'Cancel')
   */
  cancel?: string
  /**
   * Boolean to hide or show the 'cancel' button
   */
  cancel_hide?: boolean
  /**
   * Label to show in the auxiliary button, if no label is passed, the auxiliary button is not shown
   */
  aux?: string
  /**
   * Boolean to enable the auxiliary button (default: True)
   */
  aux_enabled?: boolean
  /**
   * ClassName of the auxiliary button, (default: 'sm-button sm-button-orange-outline')
   */
  aux_className?: string
  /**
   * Label to show in the auxiliary button 2, if no label is passed, the auxiliary button is not shown
   */
  aux2?: string
  /**
   * Boolean to enable the auxiliary button 2 (default: True)
   */
  aux2_enabled?: boolean
  /**
   * ClassName of the auxiliary button 2, (default: 'sm-button sm-button-purple')
   */
  aux2_className?: string
  /**
   * Label to show in the auxiliary button 3, if no label is passed, the auxiliary button is not shown
   */
  aux3?: string
  /**
   * Boolean to enable the auxiliary button 3 (default: True)
   */
  aux3_enabled?: boolean
  /**
   * ClassName of the auxiliary button 3, (default: 'sm-button sm-button-purple')
   */
  aux3_className?: string
  /**
   * If True, the modal is shown (default: False)
   */
  is_open?: boolean
  /**
   * The size of this component, it can be:
   * sm: 300 px
   * none (default): 500px
   * lg: 800px
   * xl: 1140px
   */
  size?: string
  /**
   * The children of this component.
   */
  children?: ReactNode
}

interface SmModalProps extends SmModalPropsBase {
  /**
   * Number of times the 'accept' button has been clicked
   */
  n_accept?: number

  /**
   * Number of times the auxiliary button has been clicked
   */
  n_aux?: number

  /**
   * Number of times the auxiliary button 2 has been clicked
   */
  n_aux2?: number

  /**
   * Number of times the auxiliary button 3 has been clicked
   */
  n_aux3?: number

    /**
   * Number of times the cancel button has been clicked
   */
  n_cancel?: number

  /**
   * Dash-assigned callback that should be called to report property changes
   * to Dash, to make them available for callbacks.
   */
  setProps: (props: Partial<SmModalProps>) => void
}

interface SmModalPropsReact extends SmModalPropsBase {
  /**
   * Function to set the isOpen state of the component
   */
  setIsOpen: (arg: boolean) => void

  /**
   * Function triggered when the 'accept' button is pressed
   */
  onAccept?: () => void

  /**
   * Function triggered when the 'accept' button is pressed
   */
  onCancel?: () => void

  /**
   * Function triggered when the aux button is pressed
   */
  onAux?: () => void

  /**
   * Function triggered when the aux button is pressed
   */
  onAux2?: () => void

  /**
   * Function triggered when the aux button is pressed
   */
  onAux3?: () => void
}

/**
 * SmModal is a component to display a modal
 */
export const SmModalReact = ({
  accept = "Accept",
  sticky_header = false,
  sticky_bottom = false,
  accept_enabled = true,
  cancel = "Cancel",
  cancel_hide = false,
  aux_className = DEFAULT_AUX_CLASS,
  aux_enabled = true,
  aux2_className = DEFAULT_AUX_CLASS,
  aux2_enabled = true,
  aux3_className = DEFAULT_AUX_CLASS,
  aux3_enabled = true,
  is_open = false,
  ...props
}: SmModalPropsReact) => {
  const { children, aux, aux2, aux3, onAux, onAux2, onAux3, header, size, setIsOpen, onAccept, onCancel} = props

  const style = { display: is_open ? "block" : "none" }
  const className = `modal${is_open ? " show" : ""}`
  const sizeClassName = size ? ` modal-${size}` : ""
  const dialogClassName = `modal-dialog modal-dialog-centered${sizeClassName}`
  const closeModal = () => {
    setIsOpen(false)
  }
  const onAcceptClick = onAccept !== undefined && accept_enabled ? onAccept : closeModal
  const onCancelClick = onCancel !== undefined && !cancel_hide ? onCancel : closeModal
  const onAuxClick = onAux !== undefined && aux_enabled ? onAux : closeModal
  const onAux2Click = onAux2 !== undefined && aux2_enabled ? onAux2 : closeModal
  const onAux3Click = onAux3 !== undefined && aux3_enabled ? onAux3 : closeModal

  return (
    <>
      {is_open && <div className="fade modal-backdrop show"></div>}
      <div className={className} style={style}>
        <div className={dialogClassName}>
          <div className="modal-content border-0 sm-rounded" style={{"contain": "paint"}}>
            <div className="modal-body p-0">
              <div
                className="fw-bold p-4 fs-4 bg-gray black border-bottom"
                style={sticky_header ? { position: "sticky", top: 0, zIndex: 1 } : {}}
              >
                <div className="underline">
                {header}
                </div>
              </div>
              <div className="p-4">{children}</div>
              <div
                className="a-middle-between p-4 border-top bg-white"
                style={sticky_bottom ? { position: "sticky", bottom: 0, zIndex: 1 } : {}}
              >
                {cancel_hide ?
                    "":
                <button
                  className="sm-button-glass"
                  onClick={onCancelClick}
                  style={{ width: "100px" }}
                >{cancel}</button>
                }
                <div className="a-middle gap-2">
                  {aux && (
                    <button
                      className={aux_className}
                      onClick={onAuxClick}
                      disabled={!aux_enabled}
                      style={{ minWidth: "100px" }}
                    >
                      {aux}
                    </button>
                  )}
                  {aux2 && (
                    <button
                      className={aux2_className}
                      onClick={onAux2Click}
                      disabled={!aux2_enabled}
                      style={{ minWidth: "100px" }}

                    >
                      {aux2}
                    </button>
                  )}
                  {aux3 && (
                    <button
                      className={aux3_className}
                      onClick={onAux3Click}
                      disabled={!aux3_enabled}
                      style={{ minWidth: "100px" }}

                    >
                      {aux3}
                    </button>
                  )}
                  {accept && (
                    <button
                      className={DEFAULT_ACCEPT_CLASS}
                      onClick={onAcceptClick}
                      disabled={!accept_enabled}
                      style={{ minWidth: "100px" }}
                    >
                      {accept}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

/**
 * SmModal is a component that will display a modal and allows you to control its acceptance as well as defining an
 * auxiliary button for additional functionalities.
 */
const SmModal = ({
  accept = "Accept",
  accept_enabled = true,
  cancel = "Cancel",
  is_open = false,
  sticky_header = false,
  sticky_bottom = false,
  n_accept = 0,
  n_aux = 0,
  n_aux2 = 0,
  n_aux3 = 0,
  n_cancel = 0,
  aux_enabled = true,
  aux_className = DEFAULT_AUX_CLASS,
  aux2_enabled = true,
  aux2_className = DEFAULT_AUX_CLASS,
  aux3_enabled = true,
  aux3_className = DEFAULT_AUX_CLASS,
  ...props
}: SmModalProps) => {
  const { setProps, children, ...props2 } = props
  const setIsOpen = (isOpen: boolean) => {
    setProps({ is_open: isOpen })
  }
  const onAccept = () => {
    setProps({ is_open: false, n_accept: n_accept + 1 })
  }
  const onAux = () => {
    setProps({ n_aux: n_aux + 1 })
  }
  const onAux2 = () => {
    setProps({ n_aux2: n_aux2 + 1 })
  }
  const onAux3 = () => {
    setProps({ n_aux3: n_aux3 + 1 })
  }
  const onCancel = () => {
    setProps({ is_open: false, n_cancel: n_cancel + 1 })
  }
  return (
    <>
      {createPortal(
        <SmModalReact
          accept={accept}
          sticky_header={sticky_header}
          sticky_bottom={sticky_bottom}
          accept_enabled={accept_enabled}
          cancel={cancel}
          is_open={is_open}
          setIsOpen={setIsOpen}
          onAccept={onAccept}
          onAux={onAux}
          onCancel={onCancel}
          aux_enabled={aux_enabled}
          aux_className={aux_className}
          onAux2={onAux2}
          aux2_enabled={aux2_enabled}
          aux2_className={aux2_className}
          onAux3={onAux3}
          aux3_enabled={aux3_enabled}
          aux3_className={aux3_className}
          {...props2}
        >
          {children}
        </SmModalReact>,
        document.body
      )}
    </>
  )
}

export default SmModal
